"use client";

import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { useTranslations } from "next-intl";
import { BrokerAvatar } from "@/components/brokers/broker-avatar";
import ChevronDown from "@repo/icons/react/ChevronDown";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProfessionalDesk } from "@/hooks/use-professional-desk";
import { cn } from "@/lib/utils";

export const MarketplaceAccountTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & { compact?: boolean }
>(function MarketplaceAccountTrigger({ compact, className, ...props }, ref) {
  const { user } = useCurrentUser();
  const { marketplace } = useProfessionalDesk();
  const t = useTranslations("web.marketplaceWorkspace");
  if (!user) return null;
  const selected = marketplace.data;
  const principal = selected?.principal ?? user;
  const isMember = marketplace.rememberedPath?.includes("/members/");
  const label = selected?.actingAsUserId
    ? t("actingAs", { name: principal.name ?? selected.name })
    : isMember && !selected
      ? t(marketplace.isError ? "unavailable" : "checking")
      : (user.name ?? user.email);
  return (
    <button
      {...props}
      data-marketplace-account-trigger
      ref={ref}
      type="button"
      aria-label={label}
      className={cn(
        "inline-flex h-11 items-center gap-2 rounded-full border border-border bg-secondary py-0 pl-0 pr-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:h-9",
        className,
      )}
    >
      <BrokerAvatar
        id={principal.id}
        name={principal.name}
        image={principal.image}
        isVerified={principal.isVerified}
        showVerified={false}
        size="xs"
        className="size-[42px] sm:size-[34px]"
      />
      <span
        className={cn(
          "truncate text-left",
          compact ? "max-w-[140px] text-xs" : "max-w-[210px]",
        )}
      >
        {label}
      </span>
      {!compact && (
        <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
      )}
    </button>
  );
});
